import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchMe } from '../lib/api';
import type { Message } from '../lib/api';
import { showDesktopNotification, useNotifications } from '../components/NotificationsProvider';

// Fires a desktop notification when a new message arrives that addresses
// the current operator (@-mention via `addressed_to`). Notifications are
// muted per-room via `<NotificationsProvider>` state; permission must be
// `granted` (caller's responsibility to surface the permission banner).
//
// Caller passes the current `messages` array; the hook tracks the most
// recent message_id and fires once-per-new-mention.
export function useMentionNotifier(roomId: string, messages: Message[] | undefined) {
  const { permission, isMuted } = useNotifications();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const lastSeenId = useRef<string | null>(null);

  useEffect(() => {
    if (!messages || messages.length === 0) return;
    if (permission !== 'granted') return;
    if (isMuted(roomId)) return;
    if (!me) return;

    const latest = messages[messages.length - 1];
    if (lastSeenId.current === latest.message_id) return;
    const previousMaxIndex = lastSeenId.current
      ? messages.findIndex((m) => m.message_id === lastSeenId.current)
      : -1;

    const newMessages = messages.slice(previousMaxIndex + 1);
    lastSeenId.current = latest.message_id;

    for (const msg of newMessages) {
      if (msg.sender_user_id === me.user_id) continue;
      if (msg.addressed_to !== me.display_name && msg.addressed_to !== me.email) continue;
      showDesktopNotification({
        title: `${msg.sender_persona} mentioned you`,
        body: msg.content.slice(0, 140),
        tag: `room-${roomId}`,
        onClick: () => {
          window.location.hash = `#${msg.message_id}`;
        },
      });
    }
  }, [messages, roomId, permission, isMuted, me]);
}
